'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowRight, LockKeyhole, Mail, UserRound, X } from 'lucide-react';
import type { ConfiguratorUser } from './configurator-types';
import { isValidEmail } from './configurator-workflow';
import styles from './configurator.module.css';

type AuthDialogProps = {
  open: boolean;
  onClose: () => void;
  onContinue: (user: ConfiguratorUser) => void;
};

export function ConfiguratorAuthDialog({ open, onClose, onContinue }: AuthDialogProps) {
  const [mode, setMode] = useState<'login' | 'guest'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const target =
        dialog?.querySelector<HTMLElement>('input:not([disabled])') ??
        dialog?.querySelector<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        );
      target?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose, open]);

  if (!open) return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      setError('Zadajte platnú e-mailovú adresu.');
      return;
    }
    if (mode === 'login' && password.length < 4) {
      setError('Heslo musí mať aspoň 4 znaky.');
      return;
    }

    onContinue({
      name: name.trim() || email.split('@')[0] || 'Zákazník',
      email: email.trim(),
      mode: mode === 'login' ? 'account' : 'guest',
    });
  };

  return (
    <div className={styles.dialogBackdrop} role="presentation" onMouseDown={onClose}>
      <div
        className={styles.authDialog}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        onMouseDown={event => event.stopPropagation()}
      >
        <button type="button" className={styles.dialogClose} onClick={onClose} aria-label="Zavrieť">
          <X size={19} />
        </button>

        <div className={styles.authVisual}>
          <div className={styles.authVisualGrid} />
          <Image
            className={styles.authBrandLogo}
            src="/images/branding/bdv-trade-logo.png"
            alt="BDV Trade"
            width={170}
            height={72}
            unoptimized
          />
          <div className={styles.authVisualCopy}>
            <span>Konfigurácia bez slepých miest</span>
            <h2>Od prvého rozmeru až po dáta pre výrobu.</h2>
            <p>
              Účet vám neskôr umožní ukladať rozpracované konfigurácie. Teraz môžete pokojne
              pokračovať aj bez neho.
            </p>
          </div>
          <div className={styles.authMaterialStrip}>
            <i style={{ background: '#9a6840' }} />
            <i style={{ background: '#e7e4dc' }} />
            <i style={{ background: '#a8b3b4' }} />
            <i style={{ background: '#c9d0d0' }} />
          </div>
        </div>

        <div className={styles.authFormSide}>
          <div className={styles.authTabs} role="tablist" aria-label="Spôsob pokračovania">
            <button
              type="button"
              className={mode === 'login' ? styles.authTabActive : styles.authTab}
              role="tab"
              aria-selected={mode === 'login'}
              onClick={() => {
                setMode('login');
                setError('');
              }}
            >
              Prihlásiť sa
            </button>
            <button
              type="button"
              className={mode === 'guest' ? styles.authTabActive : styles.authTab}
              role="tab"
              aria-selected={mode === 'guest'}
              onClick={() => {
                setMode('guest');
                setError('');
              }}
            >
              Bez účtu
            </button>
          </div>

          <div className={styles.authHeading}>
            <span className={styles.eyebrow}>Začíname</span>
            <h2 id="auth-title">{mode === 'login' ? 'Vitajte späť' : 'Rýchly vstup'}</h2>
            <p>
              {mode === 'login'
                ? 'Prihláste sa a konfigurácia sa spojí s vaším účtom.'
                : 'Stačí kontakt, na ktorý vám pošleme výstupy konfigurácie.'}
            </p>
          </div>

          <form className={styles.authForm} onSubmit={submit}>
            {mode === 'guest' && (
              <label className={styles.field}>
                <span>Meno a priezvisko</span>
                <div className={styles.inputWithIcon}>
                  <UserRound size={17} />
                  <input
                    value={name}
                    onChange={event => setName(event.target.value)}
                    placeholder="Ján Novák"
                    autoFocus
                  />
                </div>
              </label>
            )}

            <label className={styles.field}>
              <span>E-mail</span>
              <div className={styles.inputWithIcon}>
                <Mail size={17} />
                <input
                  type="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="jan@firma.sk"
                  autoFocus={mode === 'login'}
                />
              </div>
            </label>

            {mode === 'login' && (
              <label className={styles.field}>
                <span>Heslo</span>
                <div className={styles.inputWithIcon}>
                  <LockKeyhole size={17} />
                  <input
                    type="password"
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </label>
            )}

            {error && <p className={styles.formError}>{error}</p>}

            <button type="submit" className={styles.primaryButtonWide}>
              {mode === 'login' ? 'Prihlásiť a pokračovať' : 'Pokračovať bez účtu'}
              <ArrowRight size={18} />
            </button>

            <p className={styles.authFinePrint}>
              Toto je pripravený aplikačný flow. Reálne overenie účtu sa pripojí na backend v ďalšej
              fáze.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
